import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

interface GoldDustLoaderProps {
  isVisible: boolean;
}

export const GoldDustLoader = ({ isVisible }: GoldDustLoaderProps) => {
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    if (isVisible) {
      const newParticles: Particle[] = [];
      for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * Math.PI * 2;
        const radius = 120 + Math.random() * 60;
        newParticles.push({
          id: i,
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius,
          size: 4 + Math.random() * 8,
          delay: i * 0.05,
          duration: 1.5 + Math.random() * 0.5,
        });
      }
      setParticles(newParticles);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm"
    >
      <div className="relative w-64 h-64 flex items-center justify-center">
        {/* Central glow */}
        <motion.div
          className="absolute w-16 h-16 rounded-full bg-gold/40"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            boxShadow: "0 0 60px 30px hsl(var(--gold) / 0.3)",
          }}
        />

        {/* Spiral particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full bg-gold"
            style={{
              width: particle.size,
              height: particle.size,
              boxShadow: `0 0 ${particle.size * 2}px ${particle.size / 2}px hsl(var(--gold) / 0.5)`,
            }}
            initial={{
              x: particle.x,
              y: particle.y,
              opacity: 0,
              scale: 0,
            }}
            animate={{
              x: 0,
              y: 0,
              opacity: [0, 1, 1, 0],
              scale: [0, 1, 0.8, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* Sparkle accents */}
        {[...Array(6)].map((_, i) => (
          <motion.span
            key={`sparkle-${i}`}
            className="absolute text-gold text-xl"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1.2, 0.5],
              rotate: [0, 180],
            }}
            transition={{
              duration: 2,
              delay: i * 0.3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              left: `${50 + Math.cos((i / 6) * Math.PI * 2) * 30}%`,
              top: `${50 + Math.sin((i / 6) * Math.PI * 2) * 30}%`,
            }}
          >
            ✦
          </motion.span>
        ))}
      </div>

      {/* Loading text */}
      <motion.p
        className="mt-8 text-lg font-display text-foreground"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <motion.span
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Samlar magiskt stjärnstoft...
        </motion.span>
      </motion.p>
    </motion.div>
  );
};
